import React, { useCallback, useMemo } from 'react';
import { formatTranslation } from 'intl';
import { CommonTranslationKeys, FileTranslationKeys } from 'intl/keys';
import { useIntl } from 'react-intl';
import GetAppIcon from '@mui/icons-material/GetApp';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { DocumentationPlusIcon } from 'components/core/Navigation/NavigationDrawer/icons';
import { downloadFile } from 'services/fileOperations';
import { showGenericErrorToast } from 'services/toaster';
import { useDialog } from 'components/core/Dialog/common/DialogContext';
import {
  createDeleteDocumentDialog,
  createUnlinkDocumentationDialog,
} from 'components/core/Dialog/common/dialogTemplates';
import { useExportToDocumentationOptional } from 'components/documentation/ExportToDocumentationDialog';
import { getIssueAttachmentDisplayName } from 'services/fileService/common/getFileDisplayName';
import { canExportFileToDocumentation, signedRequestService } from 'services/fileService';
import type { DisplayedFile } from '../../gallery/filesStore';
import type { ToolbarAction } from '../../FloatingToolbar';
import type { DrawingType, GraphicUploaderGalleryMode } from '../types';
import { GRAPHIC_UPLOADER_GALLERY_MODE } from '../types';

interface UseBulkActionsParams {
  selectedIds: Set<number>;
  files: DisplayedFile[];
  drawings: (DrawingType | undefined)[];
  removeFile: (id: number) => void;
  clearSelection: () => void;
  galleryMode: GraphicUploaderGalleryMode;
  previewOnly?: boolean;
}

export function useBulkActions({
  selectedIds,
  files,
  drawings,
  removeFile,
  clearSelection,
  galleryMode,
  previewOnly,
}: UseBulkActionsParams): ToolbarAction[] {
  const intl = useIntl();
  const createDialog = useDialog();
  const exportContext = useExportToDocumentationOptional();
  const selectedFiles = useMemo(
    () =>
      Array.from(selectedIds)
        .sort((a, b) => a - b)
        .map((id) => ({ id, file: files[id] }))
        .filter(
          (entry): entry is { id: number; file: DisplayedFile } =>
            entry.file != null && !entry.file.deleted
        ),
    [selectedIds, files]
  );

  const removeSelectedFiles = useCallback(() => {
    for (const { id } of [...selectedFiles].reverse()) {
      removeFile(id);
    }
    clearSelection();
  }, [selectedFiles, removeFile, clearSelection]);

  const handleDownload = useCallback(() => {
    for (const { id, file } of selectedFiles) {
      const src = signedRequestService.resolveGalleryDownloadSource(
        file,
        drawings[id]?.mergedImageUrl
      );
      if (src) {
        downloadFile(src, file.title, file.data?.extension).catch(() =>
          showGenericErrorToast()
        );
      }
    }
    clearSelection();
  }, [selectedFiles, drawings, clearSelection]);

  const selectedFileNames = useMemo(
    () =>
      selectedFiles
        .map(({ file }) => getIssueAttachmentDisplayName(file))
        .join(', '),
    [selectedFiles]
  );

  const handleDelete = useCallback(() => {
    if (selectedFiles.length === 0) return;
    createDeleteDocumentDialog(createDialog, intl, selectedFileNames).then(
      removeSelectedFiles
    );
  }, [selectedFiles, createDialog, intl, selectedFileNames, removeSelectedFiles]);

  const handleUnlink = useCallback(() => {
    if (selectedFiles.length === 0) return;
    createUnlinkDocumentationDialog(createDialog, intl, selectedFileNames).then(
      removeSelectedFiles
    );
  }, [selectedFiles, createDialog, intl, selectedFileNames, removeSelectedFiles]);

  const filesToExport = useMemo(
    () =>
      selectedFiles
        .filter(({ file }) => canExportFileToDocumentation(file))
        .map(({ file }) => file),
    [selectedFiles]
  );

  const handleExport = useCallback(() => {
    if (!exportContext?.openExportDialog || filesToExport.length === 0) {
      return;
    }
    exportContext.openExportDialog(filesToExport);
    clearSelection();
  }, [filesToExport, exportContext, clearSelection]);

  const isLinkedDocumentation =
    galleryMode === GRAPHIC_UPLOADER_GALLERY_MODE.ISSUE_LINKED_DOCUMENTATION;

  return useMemo(() => {
    const actions: ToolbarAction[] = [
      {
        label: formatTranslation(intl, CommonTranslationKeys.download),
        icon: <GetAppIcon fontSize='small' />,
        onClick: handleDownload,
      },
    ];

    if (isLinkedDocumentation) {
      if (!previewOnly) {
        actions.push({
          label: formatTranslation(intl, FileTranslationKeys.thumbnailBulkUnlink),
          icon: <LinkOffIcon fontSize='small' />,
          onClick: handleUnlink,
        });
      }
    } else {
      if (!previewOnly) {
        actions.push({
          label: formatTranslation(intl, FileTranslationKeys.thumbnailBulkDelete),
          icon: <DeleteForeverIcon fontSize='small' />,
          onClick: handleDelete,
        });
      }

      if (exportContext?.openExportDialog) {
        actions.push({
          label: formatTranslation(intl, CommonTranslationKeys.exportToDocumentation),
          icon: <DocumentationPlusIcon fontSize='small' />,
          onClick: handleExport,
          disabled: filesToExport.length === 0,
        });
      }
    }

    return actions;
  }, [
    intl,
    handleDownload,
    handleDelete,
    handleUnlink,
    handleExport,
    filesToExport,
    isLinkedDocumentation,
    previewOnly,
    exportContext,
  ]);
}
